import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Activity, Link2, RefreshCw, Trash2, Upload, Unlink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Chip, Loader, PageHeader, Reveal } from "@/components/common";

const HOWTO = {
  xiaomi: "Mi Fitness / Zepp Life: Perfil → Ajustes → Privacidad → Exportar datos. Sube el CSV de actividades (SPORT o ACTIVITY).",
  zepp: "Zepp: Perfil → Ajustes → Exportar datos (o desde la web de Zepp). Sube el CSV o JSON.",
  adidas: "adidas Running: web → Ajustes → Privacidad → Exportar datos. Sube los archivos GPX o JSON de las sesiones.",
  garmin: "Garmin Connect (web): abre una actividad → ⚙ → Exportar original (FIT) o GPX/TCX. También vale el CSV de la lista de actividades.",
  strava: "Strava: mejor conecta tu cuenta arriba. También puedes subir el activities.csv de la exportación masiva.",
  polar: "Polar Flow: abre la sesión → Exportar → TCX, GPX o CSV.",
  suunto: "Suunto app: actividad → ⋯ → Exportar FIT/GPX.",
  apple: "Apple Salud no exporta directamente: usa una app como HealthFit para exportar FIT/GPX.",
  huawei: "Huawei Health: usa Health Sync o exporta GPX/TCX desde la actividad.",
  otro: "Cualquier archivo GPX, TCX, FIT, CSV o JSON con fecha y duración.",
};

const StravaCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/integrations/strava/status").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const connect = async () => { const { data } = await api.get("/integrations/strava/connect-url"); window.location.href = data.url; };
  const sync = async () => {
    setBusy(true);
    try { const { data } = await api.post("/integrations/strava/import"); toast.success(`${data.imported} actividades nuevas de Strava`); load(); window.dispatchEvent(new Event("activities:changed")); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const disconnect = async () => { await api.delete("/integrations/strava"); toast.success("Strava desconectado"); load(); };
  return (
    <div className="card-clay flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="strava-card">
      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FC4C02] font-display text-lg font-extrabold text-white">S</span>
        <div>
          <p className="font-display font-bold">Strava</p>
          <p className="text-sm text-stone-600" data-testid="strava-status">
            {!s.available ? "Pendiente de configurar por el administrador" : s.connected ? `Conectado${s.athlete_name ? ` como ${s.athlete_name}` : ""}${s.last_import ? ` · última sync ${fmtDate(s.last_import.slice(0, 10))}` : ""}` : "Conecta tu cuenta para importar tus actividades automáticamente"}
          </p>
        </div>
      </div>
      {s.available && (s.connected ? (
        <div className="flex gap-2">
          <button onClick={sync} disabled={busy} data-testid="strava-sync-btn" className="flex items-center gap-2 rounded-full bg-[#FC4C02] px-4 py-2.5 text-sm font-semibold text-white transition-transform active:scale-95 disabled:opacity-60"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Sincronizar</button>
          <button onClick={disconnect} data-testid="strava-disconnect-btn" className="rounded-full border border-stone-300 p-2.5"><Unlink className="h-4 w-4" /></button>
        </div>
      ) : (
        <button onClick={connect} data-testid="strava-connect-btn" className="flex items-center gap-2 rounded-full bg-[#FC4C02] px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-95"><Link2 className="h-4 w-4" /> Conectar con Strava</button>
      ))}
    </div>
  );
};

const FileImport = ({ providers }) => {
  const [prov, setProv] = useState("xiaomi");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("provider", prov);
      fd.append("file", f);
      try {
        const { data } = await api.post("/activities/import", fd);
        toast.success(`${f.name}: ${data.imported} nuevas${data.duplicates ? `, ${data.duplicates} repetidas` : ""}`);
      } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy(false);
    input.current.value = "";
    window.dispatchEvent(new Event("activities:changed"));
  };
  return (
    <div className="card-clay p-5 sm:p-6" data-testid="file-import-card">
      <p className="font-display font-bold">Subir archivo exportado</p>
      <div className="mt-4 flex flex-wrap gap-2">{providers.map((p) => <Chip key={p.id} tone="train" active={prov === p.id} onClick={() => setProv(p.id)} testid={`import-provider-${p.id}`}>{p.name}</Chip>)}</div>
      <p className="mt-4 rounded-2xl bg-train-soft p-4 text-sm text-train-ink" data-testid="import-howto">{HOWTO[prov]}</p>
      <input ref={input} type="file" multiple accept=".gpx,.tcx,.fit,.csv,.json" className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="import-file-input" />
      <button disabled={busy} onClick={() => input.current.click()} data-testid="import-upload-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-stone-300 py-6 font-semibold text-stone-600 transition-colors hover:border-train hover:text-train-ink disabled:opacity-60">
        <Upload className="h-5 w-5" /> {busy ? "Importando…" : "Elegir archivos (GPX, TCX, FIT, CSV, JSON)"}
      </button>
    </div>
  );
};

export const ActivityList = ({ day, compact }) => {
  const [list, setList] = useState(null);
  const load = () => api.get(`/activities${day ? `?day=${day}` : ""}`).then((r) => setList(r.data));
  useEffect(() => {
    load();
    window.addEventListener("activities:changed", load);
    return () => window.removeEventListener("activities:changed", load);
  }, [day]); // eslint-disable-line
  if (!list) return compact ? null : <Loader />;
  if (compact && !list.length) return null;
  const del = async (id) => { await api.delete(`/activities/${id}`); load(); };
  return (
    <div className="space-y-2" data-testid="activity-list">
      {compact && <p className="eyebrow text-train-ink">Actividad registrada este día</p>}
      {!list.length && <p className="rounded-3xl border border-dashed border-stone-300 p-6 text-center text-sm text-stone-600">Aún no has importado actividades.</p>}
      {list.map((a, i) => (
        <Reveal key={a.id} delay={Math.min(i, 8) * 0.03} className="flex items-center gap-4 rounded-2xl bg-white p-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-train-soft"><Activity className="h-5 w-5 text-train-ink" /></span>
          <div className="min-w-0 flex-1" data-testid={`activity-${i}`}>
            <p className="truncate font-semibold capitalize">{a.name || a.sport}</p>
            <p className="text-xs text-stone-500">{fmtDate(a.date)} · {a.provider}{a.duration_min ? ` · ${Math.round(a.duration_min)} min` : ""}{a.distance_km ? ` · ${a.distance_km} km` : ""}{a.kcal ? ` · ${a.kcal} kcal` : ""}{a.hr_avg ? ` · FC ${a.hr_avg}` : ""}</p>
          </div>
          {!compact && <button onClick={() => del(a.id)} data-testid={`activity-delete-${i}`} className="rounded-full p-2 hover:bg-stone-100"><Trash2 className="h-4 w-4 text-stone-400" /></button>}
        </Reveal>
      ))}
    </div>
  );
};

export default function Activities() {
  const [providers, setProviders] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => { api.get("/activities/providers").then((r) => setProviders(r.data)); }, []);
  useEffect(() => {
    const s = params.get("strava");
    if (!s) return;
    s === "connected" ? toast.success("Strava conectado. Pulsa Sincronizar.") : toast.error("No se pudo conectar con Strava");
    setParams({}, { replace: true });
  }, [params, setParams]);
  if (!providers) return <Loader />;
  return (
    <div className="space-y-5" data-testid="activities-page">
      <PageHeader eyebrow="Actividad" tone="train" title="Importa tu actividad" subtitle="Trae tus entrenos de Strava, Xiaomi, Zepp, adidas, Garmin y más. Cada día con actividad suma a tu racha." />
      <StravaCard />
      <FileImport providers={providers} />
      <h2 className="pt-2 text-lg font-bold">Historial importado</h2>
      <ActivityList />
    </div>
  );
}
